import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Grid, Skeleton, Stack, Typography } from '@mui/material';

import type { CodingResult, DashboardWidgetProps } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import {
  hideWidget,
  showWidget,
} from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/helper';
import {
  getFilteredSubtopics,
  getMaxValue,
  getMinValue,
  getRelevantTopicsWithSortedSubtopics,
  INSIGHT_WHEEL_TOPICS_MAX,
  INSIGHT_WHEEL_TOPICS_MIN,
} from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/helper';
import InsightWheel from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel';
import InsightWheelFilterGroup from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheelFilterGroup';
import InsightWheelLegend from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheelLegend';
import InsightWheelSlider from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheelSlider';
import type {
  SentimentFilter,
  Sorting,
  TopicFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import { styles } from './index.styles';

export interface InsightFilter {
  topicFilter: TopicFilter;
  sentimentFilter: SentimentFilter;
  sorting: Sorting;
}

const isValidCodingResults = (
  value: CodingResult[] | undefined,
): value is CodingResult[] => Array.isArray(value) && value.length > 0;

export const INSIGHT_WHEEL_WIDGET_TEST_ID = 'insight-wheel-widget';

const InsightWheelWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const { filter, setFilter, codingResults, loading, noData } =
    useCodingResults();
  const [selectedTopics, setSelectedTopics] = useState<CodingResult[]>(
    codingResults || [],
  );
  const filteredSubtopics = useMemo(
    () => getFilteredSubtopics(selectedTopics, filter.sentimentFilter),
    [selectedTopics, filter.sentimentFilter],
  );
  const [sliderValue, setSliderValue] = useState<number>(
    getMaxValue(filteredSubtopics),
  );

  const { createStep, removeStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => !noData.initial && createStep(config.id, config.guided_tour_step),
    [noData, config.guided_tour_step],
  );

  const widgetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (noData.initial && !loading) {
      removeStep(config.id);
      widgetRef.current = hideWidget(config.id, widgetRef.current);
    } else {
      showWidget(config.id, widgetRef.current);
      setSelectedTopics(codingResults);
      setSliderValue(
        getMaxValue(
          getFilteredSubtopics(codingResults, filter.sentimentFilter),
        ),
      );
    }
  }, [noData, loading]);

  const handleLegendTopicClick = (
    _e: React.MouseEvent<HTMLElement>,
    topic: CodingResult,
  ) => {
    const newSelectedTopics = selectedTopics.find(
      ({ categoryLabel }) => categoryLabel === topic.categoryLabel,
    )
      ? selectedTopics.filter(
          ({ categoryLabel }) => categoryLabel !== topic.categoryLabel,
        )
      : [...selectedTopics, topic];

    if (selectedTopics.length === 0) {
      // selected topics was 0
      setSliderValue(
        topic.topics.length >= INSIGHT_WHEEL_TOPICS_MIN
          ? INSIGHT_WHEEL_TOPICS_MIN
          : topic.topics.length,
      );
    } else if (newSelectedTopics.length === 0) {
      // selected topics will become 0
      setSliderValue(0);
    }

    setSelectedTopics(newSelectedTopics);
  };

  useEffect(() => {
    if (!isValidCodingResults(codingResults)) return;

    setSelectedTopics((topics) =>
      getRelevantTopicsWithSortedSubtopics(topics, codingResults),
    );
  }, [codingResults]);

  const handleSliderValue = (_e: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') setSliderValue(newValue);
  };

  const maxValue = useMemo(
    () => getMaxValue(filteredSubtopics),
    [filteredSubtopics],
  );
  const minValue = useMemo(
    () => getMinValue(filteredSubtopics),
    [filteredSubtopics],
  );

  useEffect(() => {
    setSliderValue((prevValue) =>
      maxValue < prevValue ? maxValue : prevValue,
    );
  }, [maxValue]);

  useEffect(() => {
    if (filteredSubtopics.length < INSIGHT_WHEEL_TOPICS_MIN) {
      setSliderValue(0);
    } else {
      setSliderValue((prevValue) =>
        maxValue < prevValue ? maxValue : prevValue,
      );
    }
  }, [maxValue]);

  // Do not render this widget, if essential data is missing
  if (noData.initial) {
    return null;
  }

  return (
    <Stack
      direction="column"
      spacing={2}
      data-test-id={INSIGHT_WHEEL_WIDGET_TEST_ID}
      sx={{ p: 3, maxWidth: '100%' }}
    >
      <Grid container>
        <Grid item xs={12} lg={6}>
          <Stack direction="row" spacing={1.25}>
            {guidedTour}
            <Typography
              component="span"
              variant="h3"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {config.name ? config.name : t('InsightWheel.Title')}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={6}>
          <InsightWheelSlider
            onSliderChange={handleSliderValue}
            sliderCurrentValue={sliderValue}
            sliderMinValue={minValue}
            sliderMaxValue={maxValue}
          />
        </Grid>
      </Grid>
      <Box>
        <InsightWheelFilterGroup
          setInsightWheelFilter={setFilter}
          insightWheelFilter={filter}
        />
      </Box>

      {filteredSubtopics.length > INSIGHT_WHEEL_TOPICS_MAX && (
        <Box>
          <Stack direction="row">
            <Alert severity="error" icon={false} sx={styles.subTopicHint}>
              {t('InsightWheel.SubtopicHint')}
            </Alert>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Stack direction="row" spacing={1}>
          <Stack
            direction="column"
            spacing={1}
            sx={{ maxWidth: '16rem', width: '100%' }}
          >
            {Array(15)
              .fill(null)
              .map((_, i) => (
                <Skeleton variant="rounded" key={i} />
              ))}
          </Stack>
          <Skeleton variant="rectangular" width="100%" height="45rem" />
        </Stack>
      ) : (
        <Stack direction="row">
          <Box sx={{ maxWidth: '16rem', flexGrow: 1 }}>
            <InsightWheelLegend
              toggleButtonGroupValue={selectedTopics}
              onLegendTopicClick={handleLegendTopicClick}
              codingResults={codingResults}
            />
          </Box>
          <Box sx={{ flexGrow: 1, pl: 3.5, minHeight: '28rem' }}>
            <InsightWheel
              sentimentFilter={filter.sentimentFilter}
              selectedSubtopics={filteredSubtopics}
              valueFromSlider={sliderValue}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
export default InsightWheelWidget;
